/* global FormData, CustomEvent */

/**
 * AddRatingForm Component
 *
 * Handles the rating form submission functionality.
 */
export class AddRatingForm {
	/**
	 * Constructor for the AddRatingForm class.
	 *
	 * @param {HTMLElement} element - The DOM element for the rating form.
	 */
	constructor( element ) {
		/** @type {HTMLElement} */
		this.element = element;

		/** @type {HTMLFormElement} */
		this.form = element.querySelector( '.js-qala-ratings-add-rating-form__form' );

		/** @type {HTMLButtonElement} */
		this.submitButton = element.querySelector( '.js-qala-ratings-add-rating-form__submit-button' );

		/** @type {HTMLElement} */
		this.messageContainer = element.querySelector( '.js-qala-ratings-add-rating-form__message' );

		/** @type {string} */
		this.apiUrl = '/wp-json/qala-ratings/v1/submit-rating';

		/** @type {boolean} */
		this.isSubmitting = false;

		this.localizations = window.QalaRatings.localizations || {};
	}

	/**
	 * Initialize the AddRatingForm component.
	 *
	 * @return {void}
	 * @public
	 */
	init() {
		if ( ! this.form ) {
			return;
		}

		this.form.addEventListener( 'submit', event => this.handleSubmit( event ) );
		this.submitButton.disabled = false;
	}

	/**
	 * Handle form submission.
	 *
	 * @param {Event} event - The submit event.
	 * @return {void}
	 */
	async handleSubmit( event ) {
		event.preventDefault();

		if ( this.isSubmitting ) {
			return;
		}

		// Clear previous messages.
		this.clearMessage();

		// Validate form.
		if ( ! this.validateForm() ) {
			return;
		}

		// Get form data.
		const formData = this.getFormData();

		// Submit the rating.
		this.isSubmitting = true;
		this.setLoadingState( true );

		try {
			const response = await this.submitRating( formData );
			this.handleSuccess( response );
		} catch ( error ) {
			this.handleError( error );
		} finally {
			this.isSubmitting = false;
			this.setLoadingState( false );
		}
	}

	/**
	 * Validate the form.
	 *
	 * @return {boolean} True if valid, false otherwise.
	 */
	validateForm() {
		const ratingInput = this.form.querySelector( 'input[name="qala_ratings_rating_value"]' );
		const rating = parseFloat( ratingInput?.value || 0 );

		if ( ! rating || rating <= 0 ) {
			this.showMessage( this.localizations.noRatingError, 'error' );
			return false;
		}

		return true;
	}

	/**
	 * Get form data as an object.
	 *
	 * @return {Object} The form data.
	 */
	getFormData() {
		const formData = new FormData( this.form );
		const postId = this.element.dataset.postId || null;
		const customFields = this.element.querySelectorAll( '.js-qala-ratings-form-field__input--custom-field' );

		const data = {
			post_id: parseInt( postId, 10 ),
			rating: parseFloat( formData.get( 'qala_ratings_rating_value' ) || 0 ),
			title: formData.get( 'title' ) || '',
			comment: formData.get( 'comment' ) || '',
			name: formData.get( 'name' ) || '',
			fields: {},
		};

		customFields.forEach( field => {
			const fieldName = field.name;
			const fieldValue = field.value;
			data.fields[ fieldName ] = fieldValue;
		} );

		return data;
	}

	/**
	 * Submit the rating to the REST API.
	 *
	 * @param {Object} data - The rating data.
	 * @return {Promise<Object>} The response data.
	 */
	async submitRating( data ) {
		const response = await fetch( this.apiUrl, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'X-WP-Nonce': this.getNonce(),
			},
			body: JSON.stringify( data ),
		} );

		const responseData = await response.json();

		if ( ! response.ok ) {
			throw new Error( responseData.message || this.localizations.submitError );
		}

		return responseData;
	}

	/**
	 * Get the WordPress REST API nonce.
	 *
	 * @return {string} The nonce.
	 */
	getNonce() {
		return window.QalaRatings?.nonce || '';
	}

	/**
	 * Handle successful submission.
	 *
	 * @param {Object} response - The response data.
	 * @return {void}
	 */
	handleSuccess( response ) {
		const message = response.message || this.localizations.ratingSubmittedSuccess;
		this.showMessage( message, 'success' );
		this.form.reset();

		// Dispatch custom event for other components to listen to.
		const event = new CustomEvent( 'qalaRatingSubmitted', {
			detail: response.data,
		} );
		this.element.dispatchEvent( event );
	}

	/**
	 * Handle submission error.
	 *
	 * @param {Error} error - The error object.
	 * @return {void}
	 */
	handleError( error ) {
		const message = error.message || this.localizations.genericError;
		this.showMessage( message, 'error' );
	}

	/**
	 * Show a message to the user.
	 *
	 * @param {string} message - The message to show.
	 * @param {string} type    - The message type ('success' or 'error').
	 * @return {void}
	 */
	showMessage( message, type ) {
		if ( ! this.messageContainer ) {
			return;
		}

		this.messageContainer.textContent = message;
		this.messageContainer.classList.remove( 'qala-ratings-add-rating-form__message--success', 'qala-ratings-add-rating-form__message--error' );
		this.messageContainer.classList.add( `qala-ratings-add-rating-form__message--${ type }` );
		this.messageContainer.style.display = 'block';

		// Scroll message into view.
		this.messageContainer.scrollIntoView( { behavior: 'smooth', block: 'nearest' } );
	}

	/**
	 * Clear any displayed message.
	 *
	 * @return {void}
	 */
	clearMessage() {
		if ( ! this.messageContainer ) {
			return;
		}

		this.messageContainer.textContent = '';
		this.messageContainer.style.display = 'none';
		this.messageContainer.classList.remove( 'qala-ratings-add-rating-form__message--success', 'qala-ratings-add-rating-form__message--error' );
	}

	/**
	 * Set the loading state of the form.
	 *
	 * @param {boolean} isLoading - Whether the form is loading.
	 * @return {void}
	 */
	setLoadingState( isLoading ) {
		if ( this.submitButton ) {
			this.submitButton.disabled = isLoading;
			this.submitButton.textContent = isLoading ? this.localizations.submittingText : this.localizations.submitButtonText;
		}

		// Disable all form inputs.
		const inputs = this.form.querySelectorAll( 'input, textarea' );
		inputs.forEach( input => {
			input.disabled = isLoading;
		} );
	}
}
